/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import shareConsolidationSubdivisionService from "../../services/shareConsolidationSubdivision";

// ── Thunks ────────────────────────────────────────────────────────────────────

/**
 * Hit the backend validation endpoint before the user confirms.
 * Returns warnings / errors about the current share distribution.
 */
export const validateConsolidationSubdivisionAsync = createAsyncThunk(
  "shareConsolidationSubdivision/validate",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareConsolidationSubdivisionService.validate(data);
      if (!res.status) {
        return rejectWithValue(res.message || "Validation failed");
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred during validation.";
      return rejectWithValue(message);
    }
  }
);

/**
 * Persist a confirmed consolidation or subdivision transaction.
 */
export const submitConsolidationSubdivisionAsync = createAsyncThunk(
  "shareConsolidationSubdivision/submit",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareConsolidationSubdivisionService.submit(data);
      if (!res.status) {
        return rejectWithValue(
          res.message || "Failed to submit consolidation / subdivision"
        );
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred during submission.";
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch all consolidation / subdivision records for a company.
 */
export const getConsolidationSubdivisionsAsync = createAsyncThunk(
  "shareConsolidationSubdivision/getList",
  async (companyId, { rejectWithValue }) => {
    try {
      const res =
        await shareConsolidationSubdivisionService.getList(companyId);
      if (!res) {
        return rejectWithValue("Failed to fetch records: invalid response");
      }
      return {
        items: res.items ?? [],
        totalCount: res.totalCount ?? 0,
      };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch a single record by id (useful when revisiting a pending transaction).
 */
export const getConsolidationSubdivisionByIdAsync = createAsyncThunk(
  "shareConsolidationSubdivision/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await shareConsolidationSubdivisionService.getById(id);
      if (!res) {
        return rejectWithValue("Record not found");
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

/**
 * Update an existing pending record.
 */
export const updateConsolidationSubdivisionAsync = createAsyncThunk(
  "shareConsolidationSubdivision/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await shareConsolidationSubdivisionService.update(id, data);
      if (!res) {
        return rejectWithValue("Update failed: invalid response");
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred during update.";
      return rejectWithValue(message);
    }
  }
);

/**
 * Delete / reverse a pending record.
 */
export const deleteConsolidationSubdivisionAsync = createAsyncThunk(
  "shareConsolidationSubdivision/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await shareConsolidationSubdivisionService.remove(id);
      if (!res) {
        return rejectWithValue("Delete failed: invalid response");
      }
      return { id };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred during deletion.";
      return rejectWithValue(message);
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const shareConsolidationSubdivisionSlice = createSlice({
  name: "shareConsolidationSubdivision",
  initialState: {
    // List of persisted records for the current company
    records: [],
    totalCount: 0,

    // The record currently being viewed / edited
    currentRecord: null,

    // Result from the backend validation call — used to surface
    // distribution-level warnings before the user confirms submit.
    // Shape: { valid: boolean, warnings: string[], errors: string[] } | null
    validationResult: null,

    // Loading / error states per operation
    isValidating: false,
    isSubmitting: false,
    isLoading: false,

    validationError: null,
    submitError: null,
    loadError: null,
  },
  reducers: {
    /** Clear the validation result when the form values change. */
    clearValidationResult: (state) => {
      state.validationResult = null;
      state.validationError = null;
    },
    /** Clear submit error (e.g. when the user edits after a failed submit). */
    clearSubmitError: (state) => {
      state.submitError = null;
    },
    /** Reset the entire slice (e.g. on unmount). */
    resetConsolidationSubdivision: (state) => {
      state.records = [];
      state.totalCount = 0;
      state.currentRecord = null;
      state.validationResult = null;
      state.isValidating = false;
      state.isSubmitting = false;
      state.isLoading = false;
      state.validationError = null;
      state.submitError = null;
      state.loadError = null;
    },
  },
  extraReducers: (builder) => {
    // ── validate ────────────────────────────────────────────────────────────
    builder
      .addCase(validateConsolidationSubdivisionAsync.pending, (state) => {
        state.isValidating = true;
        state.validationResult = null;
        state.validationError = null;
      })
      .addCase(
        validateConsolidationSubdivisionAsync.fulfilled,
        (state, action) => {
          state.isValidating = false;
          state.validationResult = action.payload.data ?? action.payload;
        }
      )
      .addCase(
        validateConsolidationSubdivisionAsync.rejected,
        (state, action) => {
          state.isValidating = false;
          state.validationError = action.payload;
        }
      );

    // ── submit ──────────────────────────────────────────────────────────────
    builder
      .addCase(submitConsolidationSubdivisionAsync.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = null;
      })
      .addCase(
        submitConsolidationSubdivisionAsync.fulfilled,
        (state, action) => {
          state.isSubmitting = false;
          const record = action.payload.data ?? action.payload;
          if (record?._id) {
            state.records.push(record);
            state.totalCount += 1;
            state.currentRecord = record;
          }
          // Clear validation result after a successful submit
          state.validationResult = null;
        }
      )
      .addCase(
        submitConsolidationSubdivisionAsync.rejected,
        (state, action) => {
          state.isSubmitting = false;
          state.submitError = action.payload;
        }
      );

    // ── getList ─────────────────────────────────────────────────────────────
    builder
      .addCase(getConsolidationSubdivisionsAsync.pending, (state) => {
        state.isLoading = true;
        state.loadError = null;
      })
      .addCase(
        getConsolidationSubdivisionsAsync.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.records = action.payload.items;
          state.totalCount = action.payload.totalCount;
        }
      )
      .addCase(getConsolidationSubdivisionsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.loadError = action.payload;
      });

    // ── getById ─────────────────────────────────────────────────────────────
    builder
      .addCase(getConsolidationSubdivisionByIdAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        getConsolidationSubdivisionByIdAsync.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.currentRecord = action.payload.data ?? action.payload;
        }
      )
      .addCase(
        getConsolidationSubdivisionByIdAsync.rejected,
        (state, action) => {
          state.isLoading = false;
          state.loadError = action.payload;
        }
      );

    // ── update ──────────────────────────────────────────────────────────────
    builder
      .addCase(updateConsolidationSubdivisionAsync.fulfilled, (state, action) => {
        const updated = action.payload.data ?? action.payload;
        const index = state.records.findIndex((r) => r._id === updated._id);
        if (index > -1) {
          state.records.splice(index, 1, updated);
        }
        if (state.currentRecord?._id === updated._id) {
          state.currentRecord = updated;
        }
      });

    // ── delete ──────────────────────────────────────────────────────────────
    builder
      .addCase(
        deleteConsolidationSubdivisionAsync.fulfilled,
        (state, action) => {
          const { id } = action.payload;
          const index = state.records.findIndex((r) => r._id === id);
          if (index > -1) {
            state.records.splice(index, 1);
            state.totalCount -= 1;
          }
          if (state.currentRecord?._id === id) {
            state.currentRecord = null;
          }
        }
      );
  },
});

export const {
  clearValidationResult,
  clearSubmitError,
  resetConsolidationSubdivision,
} = shareConsolidationSubdivisionSlice.actions;

export default shareConsolidationSubdivisionSlice.reducer;